import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Textarea } from '~/ui/textarea'
import { Switch } from '~/ui/switch'
import { Label } from '~/ui/label'
import { Card, CardContent } from '~/ui/card'
import { Badge } from '~/ui/badge'
import { Progress } from '~/ui/progress'
import {
  Lightbulb,
  Coffee,
  Utensils,
  Dumbbell,
  Waves,
  Building,
  Music,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react'
import { useSubscription } from '@apollo/client'
import { gql } from '~/__generated__'

const formSchema = z
  .object({
    name: z.string().min(1, 'Playlist name is required'),
    ai_generated: z.boolean().default(false),
    ai_prompt: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.ai_generated && (!data.ai_prompt || data.ai_prompt.trim().length === 0)) {
        return false
      }
      return true
    },
    {
      message: 'AI prompt is required when creating an AI-generated playlist',
      path: ['ai_prompt'],
    }
  )

type FormValues = z.infer<typeof formSchema>

interface AIPlaylistFormProps {
  onSubmit: (data: FormValues) => Promise<{ playlistId?: string | null }>
  isLoading?: boolean
  onClose?: () => void
}

// Enhanced business-focused prompt examples
const PROMPT_EXAMPLES = [
  {
    icon: <Coffee className="h-4 w-4" />,
    category: 'Coffee Shop',
    title: 'Morning Rush Energy',
    prompt:
      'Upbeat coffee shop music for morning rush (7-10am) that energizes customers but allows conversation. Indie pop and acoustic vibes, nothing too intense.',
    tags: ['energetic', 'conversational', 'indie', 'acoustic'],
    context: 'coffee_shop',
  },
  {
    icon: <Coffee className="h-4 w-4" />,
    category: 'Coffee Shop',
    title: 'Study Session Ambiance',
    prompt:
      'Background music for coffee shop study area - focus-friendly, non-distracting, steady tempo. Think instrumental indie and ambient electronic.',
    tags: ['focus', 'instrumental', 'ambient'],
    context: 'coffee_shop',
  },
  {
    icon: <Utensils className="h-4 w-4" />,
    category: 'Restaurant',
    title: 'Fine Dining Atmosphere',
    prompt:
      'Sophisticated dinner music for upscale restaurant that enhances romantic atmosphere without overwhelming conversation. Classical crossover and smooth jazz preferred.',
    tags: ['sophisticated', 'romantic', 'jazz', 'classical'],
    context: 'restaurant',
  },
  {
    icon: <Utensils className="h-4 w-4" />,
    category: 'Restaurant',
    title: 'Casual Lunch Vibes',
    prompt:
      'Pleasant background music for casual lunch service - upbeat but not distracting, family-friendly, contemporary pop and indie.',
    tags: ['casual', 'family-friendly', 'contemporary'],
    context: 'restaurant',
  },
  {
    icon: <Dumbbell className="h-4 w-4" />,
    category: 'Fitness',
    title: 'High-Intensity Workout',
    prompt:
      'High-energy workout music for CrossFit gym - motivational and driving for intense 45-minute sessions. Electronic, rock, and hip-hop welcome, fast tempo.',
    tags: ['high-energy', 'motivational', 'electronic', 'rock'],
    context: 'gym',
  },
  {
    icon: <Dumbbell className="h-4 w-4" />,
    category: 'Fitness',
    title: 'Yoga & Mindfulness',
    prompt:
      'Calming music for yoga and meditation classes - peaceful, flowing, mostly instrumental. Think ambient, world music, and acoustic sounds.',
    tags: ['calming', 'instrumental', 'ambient', 'world'],
    context: 'gym',
  },
  {
    icon: <Waves className="h-4 w-4" />,
    category: 'Spa & Wellness',
    title: 'Therapeutic Relaxation',
    prompt:
      'Relaxing spa music for massage therapy sessions - purely instrumental, very calming, promotes deep relaxation. Ambient, new age, and nature sounds.',
    tags: ['relaxing', 'therapeutic', 'instrumental', 'ambient'],
    context: 'spa',
  },
  {
    icon: <Building className="h-4 w-4" />,
    category: 'Professional',
    title: 'Office Background',
    prompt:
      'Professional office background music - focus-enhancing, non-distracting, steady tempo. Instrumental and soft contemporary sounds for productivity.',
    tags: ['professional', 'focus', 'instrumental', 'productivity'],
    context: 'office',
  },
  {
    icon: <Music className="h-4 w-4" />,
    category: 'Retail',
    title: 'Shopping Experience',
    prompt:
      'Upbeat retail background music for fashion boutique - trendy, brand-aligned, energetic but not overwhelming. Contemporary pop and indie hits.',
    tags: ['trendy', 'retail', 'contemporary', 'upbeat'],
    context: 'retail',
  },
]

const PLAYLIST_GENERATION_SUBSCRIPTION = gql(`
  subscription PlaylistGenerationStatus($playlistId: ID!) {
    playlistGenerationStatus(playlistId: $playlistId) {
      playlistId
      status
      progress
      tracksGenerated
      totalTracks
      message
      completedAt
      error
      confidence
      businessContext
      extractedAttributes
    }
  }
`)

export const AIPlaylistForm: React.FC<AIPlaylistFormProps> = ({
  onSubmit,
  isLoading = false,
  onClose,
}) => {
  const [selectedExample, setSelectedExample] = useState<(typeof PROMPT_EXAMPLES)[0] | null>(null)
  const [showAllExamples, setShowAllExamples] = useState(false)
  const [generatingPlaylistId, setGeneratingPlaylistId] = useState<string | null>(null)
  const [generationStatus, setGenerationStatus] = useState<any>(null)
  const isGenerating = Boolean(generatingPlaylistId)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      ai_generated: false,
      ai_prompt: '',
    },
  })

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form
  const aiGenerated = watch('ai_generated')

  // Subscribe to playlist generation status
  useSubscription(PLAYLIST_GENERATION_SUBSCRIPTION, {
    variables: { playlistId: generatingPlaylistId ?? '' },
    skip: !isGenerating,
    onData: ({ data }) => {
      if (data?.data?.playlistGenerationStatus) {
        setGenerationStatus(data.data.playlistGenerationStatus)
      }
    },
  })

  const handleFormSubmit = async (data: FormValues) => {
    try {
      const result = await onSubmit(data)

      // If this is AI-generated, start tracking generation updates
      if (result?.playlistId && (data.ai_generated || Boolean(data.ai_prompt?.trim()))) {
        setGeneratingPlaylistId(result.playlistId)
      }
    } catch (error) {
      console.error('Error creating playlist:', error)
    }
  }

  const handleExampleSelect = (example: (typeof PROMPT_EXAMPLES)[0]) => {
    setValue('ai_prompt', example.prompt)
    setValue('name', form.getValues('name') || `${example.category} - ${example.title}`)
    setSelectedExample(example)
  }

  const displayedExamples = showAllExamples ? PROMPT_EXAMPLES : PROMPT_EXAMPLES.slice(0, 6)
  const groupedExamples = displayedExamples.reduce(
    (acc, example) => {
      if (!acc[example.category]) acc[example.category] = []
      acc[example.category].push(example)
      return acc
    },
    {} as Record<string, typeof PROMPT_EXAMPLES>
  )

  // Reset generation tracking when dialog closes
  useEffect(() => {
    if (!isLoading && generationStatus?.status === 'completed') {
      // Auto-close after successful generation
      setTimeout(() => {
        setGeneratingPlaylistId(null)
        setGenerationStatus(null)
        if (onClose) onClose()
      }, 2000)
    }
  }, [generationStatus?.status, isLoading, onClose])

  const renderGenerationStatus = () => {
    if (!generationStatus) return null

    const {
      status,
      progress,
      tracksGenerated,
      totalTracks,
      message,
      error,
      confidence,
      businessContext,
    } = generationStatus

    return (
      <Card className="mt-4 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-blue-900">
                {status === 'completed' ? 'Generation Complete!' : 'Generating Playlist...'}
              </h4>
              {status === 'completed' && <CheckCircle className="h-5 w-5 text-green-600" />}
              {status === 'failed' && <AlertCircle className="h-5 w-5 text-red-600" />}
              {status === 'processing' && <Clock className="h-5 w-5 animate-spin text-blue-600" />}
            </div>

            <Progress value={progress * 100} className="w-full" />

            <div className="flex justify-between text-xs text-blue-700">
              <span>{message}</span>
              <span>{Math.round(progress * 100)}%</span>
            </div>

            {tracksGenerated > 0 && (
              <div className="text-xs text-blue-700">
                Generated {tracksGenerated} of {totalTracks} tracks
              </div>
            )}

            {businessContext && confidence && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant="outline" className="border-blue-300 text-blue-700">
                  Context: {businessContext}
                </Badge>
                <Badge variant="outline" className="border-blue-300 text-blue-700">
                  Confidence: {Math.round(confidence * 100)}%
                </Badge>
              </div>
            )}

            {error && <div className="rounded bg-red-100 p-2 text-xs text-red-700">{error}</div>}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">Playlist Name</Label>
          <Input
            id="name"
            {...form.register('name')}
            placeholder="Enter playlist name"
            disabled={isLoading || isGenerating}
          />
          {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="ai-generated"
            checked={aiGenerated}
            onCheckedChange={(checked) => setValue('ai_generated', checked)}
            disabled={isLoading || isGenerating}
          />
          <Label htmlFor="ai-generated" className="flex items-center space-x-2">
            <Sparkles className="h-4 w-4" />
            <span>AI Generated Playlist</span>
          </Label>
        </div>

        {aiGenerated && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ai-prompt">Business Context & Music Description</Label>
              <Textarea
                id="ai-prompt"
                {...form.register('ai_prompt')}
                placeholder="Describe your ideal playlist for your business context (e.g., 'upbeat coffee shop music for morning rush with indie and pop vibes')"
                className="min-h-[100px]"
                disabled={isLoading || isGenerating}
              />
              {errors.ai_prompt && (
                <p className="text-sm text-red-600">{errors.ai_prompt.message}</p>
              )}
              <p className="text-muted-foreground text-sm">
                Be specific about your venue type, time of day, desired energy level, and musical
                preferences. Our AI understands business contexts and will suggest appropriate
                music.
              </p>
            </div>

            {/* Prompt Examples */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Example Prompts by Business Type</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllExamples(!showAllExamples)}
                  disabled={isLoading || isGenerating}
                >
                  {showAllExamples ? 'Show Less' : 'Show All Examples'}
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(groupedExamples).map(([category, examples]) => (
                  <div key={category} className="space-y-2">
                    <h4 className="text-muted-foreground text-sm font-medium">{category}</h4>
                    {examples.map((example, index) => (
                      <Card
                        key={`${category}-${index}`}
                        className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                          selectedExample === example ? 'ring-primary ring-2' : ''
                        } ${isLoading || isGenerating ? 'cursor-not-allowed opacity-50' : ''}`}
                        onClick={() =>
                          !isLoading && !isGenerating && handleExampleSelect(example)
                        }
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start space-x-3">
                            <div className="mt-1 flex-shrink-0">{example.icon}</div>
                            <div className="min-w-0 flex-1">
                              <h5 className="mb-1 text-sm font-medium">{example.title}</h5>
                              <p className="text-muted-foreground mb-2 line-clamp-2 text-xs">
                                {example.prompt}
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {example.tags.map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* AI Processing Info */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <Lightbulb className="mt-0.5 h-5 w-5 text-blue-600" />
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium text-blue-900">AI Music Curation</h4>
                    <p className="text-sm text-blue-700">
                      Our AI analyzes your prompt to understand business context, energy levels,
                      musical preferences, and appropriateness factors. It then curates tracks from
                      our licensed library based on audio features like tempo, energy, mood, and
                      genre compatibility.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge variant="outline" className="border-blue-300 text-blue-700">
                        Business Context Analysis
                      </Badge>
                      <Badge variant="outline" className="border-blue-300 text-blue-700">
                        Audio Feature Matching
                      </Badge>
                      <Badge variant="outline" className="border-blue-300 text-blue-700">
                        Appropriate Content Filtering
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Generation Status */}
            {renderGenerationStatus()}
          </div>
        )}

        <div className="flex space-x-3">
          <Button type="submit" disabled={isLoading || isGenerating} className="flex-1">
            {isLoading || isGenerating ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                {aiGenerated ? 'Generating Playlist...' : 'Creating Playlist...'}
              </>
            ) : aiGenerated ? (
              'Generate AI Playlist'
            ) : (
              'Create Playlist'
            )}
          </Button>
          {onClose && (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading || isGenerating}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}

export default AIPlaylistForm
